import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import ProfileDataContext from '@/context/ProfileDataContext';
import useScreenSize from '@/hooks/useScreenSize';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import ForwardArrowIcon from '@/shared-components/icons/components/public/ForwardArrowIcon';
import ArtistVerifiedIcon from '@/shared-components/icons/components/user-profile/ArtistVerifiedIcon';
import Text from '@/shared-components/text/components/Text';

import './styles/ArtistArtworksHeader.css';

function resolveArtistProfileImageId(artistData) {
  const candidates = [
    artistData?.user_image,
    artistData?.artist_image,
    artistData?.profile_picture_for_gallery,
    artistData?.gallery_profile_picture,
    artistData?.profile_picture,
    artistData?.picture,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
    if (typeof candidate === 'object') {
      const imageId = candidate.image_id ?? candidate.image ?? candidate.id ?? null;
      if (typeof imageId === 'string' && imageId.trim().length > 0) {
        return imageId.trim();
      }
    }
  }

  return null;
}

function buildArtistMetaLine(artistData, isArtist) {
  if (!artistData || typeof artistData !== 'object') return '';

  const nationality = typeof artistData.artist_nationality === 'string'
    ? artistData.artist_nationality.trim()
    : '';
  const birth = typeof artistData.artist_birth === 'string'
    ? artistData.artist_birth.trim()
    : '';
  const death = typeof artistData.artist_death === 'string'
    ? artistData.artist_death.trim()
    : '';

  const leadParts = [nationality, birth].filter(Boolean);
  const lead = leadParts.join(', ');
  const withDeath = death ? `${lead ? `${lead}-` : '-'}${death}` : lead;

  if (isArtist) {
    const username = typeof artistData.user_username === 'string'
      ? artistData.user_username.trim()
      : '';
    if (username && withDeath) return `@${username} - ${withDeath}`;
    if (username) return `@${username}`;
    return withDeath;
  }

  return withDeath;
}

export default function ArtistArtworksHeader({ artistData, openEditArtist, preview=false }) {
  const { profileData, internal } = useContext(ProfileDataContext);
  const screenSize = useScreenSize();

  const isArtist = artistData?.user_type === 'artist';
  const isGalleryArtist = artistData?.user_type === 'gallery_artist';
  const artistProfileImageId = resolveArtistProfileImageId(artistData);

  const artistProfileUrl = `/user/${artistData?.user_username}`;
  const navigate = useNavigate();

  const goToProfile = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(artistProfileUrl);
  };

  const visitProfileLabel = screenSize.width <= 550 ? 'Visit' : 'Visit profile';
  const artistMetaLine = buildArtistMetaLine(artistData, isArtist);

  return (
    <>
      <div className="artist-artworks-header__artist-section element-non-selectable">
        <div className="artist-artworks-header__artist-info">
          {/* Imagen: para gallery_artist mantenemos la imagen, sin enlaces */}
          {Boolean(isGalleryArtist) && (
            <UserProfileImage
              image={artistProfileImageId}
              typeOfProfile="gallery_artist"
              className={`artist-artworks-header__artist-image ${preview ? "preview" : ""}`}
            />
          )}
          {Boolean(isArtist) && (
            <UserProfileImage
              image={artistProfileImageId}
              typeOfProfile="artist"
              className={`artist-artworks-header__artist-image ${preview ? "preview" : ""}`}
            />
          )}

          <div className="artist-artworks-header__artist-name-section">
            {isArtist ? (
              <>
                <Text
                  className="artist-artworks-header__artist-name-text name font-size-small"
                  paragraph>
                  {artistData.user_name}
                  {artistData.user_flags?.suarteroad_completed && (
                    <ArtistVerifiedIcon className="artist-artworks-header__artist-verified-icon" />
                  )}
                </Text>
                <Text
                  className="artist-artworks-header__artist-name-text username font-size-small"
                  paragraph>
                  {artistMetaLine}
                </Text>
              </>
            ) : (
              /* isArtist */
              (<>
                <Text
                  className="artist-artworks-header__artist-name-text name"
                  paragraph>
                  {artistData.artist_name}
                </Text>
                <Text
                  className="artist-artworks-header__artist-name-text username"
                  paragraph>
                  {artistMetaLine}
                </Text>
              </>)
            )}
          </div>

          {/* Flecha visual (no es enlace) */}
          {!preview && (
            <ForwardArrowIcon
              className={`artist-artworks-header__artist-enter-preview${!artistData?.artist_username ? " light-offset" : ""}`}
            />
          )}
        </div>

        {isArtist ? (
          <div className="artist-artworks-header__actions">
            <ContinueButton
              className="artist-artworks-header__visit-profile"
              onClick={goToProfile}
            >
              {visitProfileLabel}
            </ContinueButton>

            {!preview && internal && (artistData?.gallery?._id === profileData?._id) && (
              <ContinueButton
                className="artist-artworks-header__edit-artist font-size-medium"
                onClick={(e) => { e.stopPropagation(); openEditArtist(artistData); }}
              >
                Edit artist
              </ContinueButton>
            )}
          </div>
        ) : (
          null
        )}
      </div>
    </>
  );
}
